<template>
    <CardLoader v-if="loading" />
    <div class="" v-else>
        <!-- Permission denied banner -->
        <div v-if="!skillPermission.canEdit && skillPermission.reason" class="alert alert-warning d-flex align-items-center gap-2 mb-3">
            <i class="material-icons">lock</i>
            <span>{{ skillPermission.reason }}</span>
        </div>

        <div class="row g-3">
            <div class="col-md-6">
                <material-input id="skill-id" label="ID" v-model="skillData.id" disabled />
            </div>
            <div class="col-md-6">
                <material-input id="skill-name" label="Name" v-model="skillData.name" required :disabled="!skillPermission.canEdit" />
            </div>
            <div class="col-md-12">
                <material-input id="skill-description" label="Description" v-model="skillData.description" :disabled="!skillPermission.canEdit" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Default Skill</label>
                    <HelpTextTooltip helpKey="skill_detail_defaultskill" />
                </div>
                <material-input id="skill-default-skill" v-model="skillData.defaultSkill" disabled />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Auto Add Person</label>
                    <HelpTextTooltip helpKey="skill_detail_autoadd" />
                </div>
                <MaterialSelect v-model="skillData.autoAddPerson" :options="yesNoOptions" :disabled="!skillPermission.canEdit" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Publicly Viewable</label>
                    <HelpTextTooltip helpKey="skill_detail_publicview" />
                </div>
                <MaterialSelect v-model="skillData.publiclyViewable"
                    :options="yesNoOptions" :disabled="!skillPermission.canEdit" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Pre-select during registration</label>
                    <HelpTextTooltip helpKey="skill_detail_preselect" />
                </div>
                <MaterialSelect v-model="skillData.preselectRegistration"
                    :options="yesNoOptions" :disabled="!skillPermission.canEdit" />
            </div>
        </div>

        <!-- Save Button -->
        <div class="d-flex justify-content-end mt-4">
            <MaterialButton @click="saveSkill" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving || !skillPermission.canEdit">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                <i v-else class="material-icons text-lg me-1">save</i>
                {{ isSaving ? 'Saving...' : 'Save' }}
            </MaterialButton>
        </div>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref, computed, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useRoute } from "vue-router";
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSelect from "@/components/common/MaterialSelect.vue";
import HelpTextTooltip from "@/components/common/HelpTextTooltip.vue";
import store from "../../../store";
import { getLocationId } from "../../../utils/SessionDataHandler";
import CardLoader from "../../../components/Loader/CardLoader.vue";
import { usePermissions } from "@/composables/usePermissions";

const props = defineProps({
    skillId: {
        type: [Number, String],
        default: null,
    },
    locationId: {
        type: [Number, String],
        default: null,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const toast = useToast();
const route = useRoute();

// Use props if provided, otherwise fallback to route params
const skillId = computed(() => {
    return props.skillId !== null ? Number(props.skillId) : Number(route.params.skillid);
});

const locationId = computed(() => {
    return props.locationId !== null ? Number(props.locationId) : getLocationId();
});

const { canModifySkill } = usePermissions();

const loading = ref(false);
const isSaving = ref(false);
const baseline = ref(null);

// Permission check – re-evaluates when skillId changes.
// When readOnly prop is true (opened via view icon), force read-only.
const skillPermission = computed(() => {
    if (props.readOnly) return { canEdit: false, reason: "" };
    return canModifySkill(skillId.value);
});

const hasChanges = computed(() => {
    if (!baseline.value) return false;
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...skillData });
});

const skillData = reactive({
    id: "",
    name: "",
    description: "",
    defaultSkill: "",
    autoAddPerson: false,
    publiclyViewable: false,
    preselectRegistration: false,
});

const yesNoOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
];

// Function to load skill data
const loadSkillData = async () => {
    if (!skillId.value) return;
    
    loading.value = true;
    try {
        await store.dispatch("getSkillById", skillId.value);
        const skill = store.getters.skill;

        if (!skill) {
            toast.error("Skill not found");
            return;
        }

        Object.assign(skillData, {
            id: skill.id,
            name: skill.name,
            description: skill.description,
            defaultSkill: skill.isDefault,
            autoAddPerson: skill.interestAutoAdd,
            publiclyViewable: skill.publicView,
            preselectRegistration: skill.preSelect,
        });
        baseline.value = { ...skillData };
    } catch (error) {
        toast.error("Failed to load skill details");
    }
    finally {
        loading.value = false;
    }
};

async function saveSkill() {
    isSaving.value = true;
    const payload = {
        skillId: skillData.id,
        locationId: locationId.value,
        name: skillData.name,
        description: skillData.description,
        autoAddPerson: skillData.autoAddPerson,
        publiclyViewable: skillData.publiclyViewable,
        preselectRegistration: skillData.preselectRegistration,
    };

    try {
        const response = await store.dispatch("saveSkillDetails", payload);

        if (response) {
            console.log(response);
            baseline.value = { ...skillData };
            toast.success(response.message, { position: "top-right" });
        }
    } catch (error) {
        toast.error("Failed to save skill details");
    }
    finally {
        isSaving.value = false;
    }
}

// Load data on mount
onMounted(() => {
    loadSkillData();
});

// Watch for skillId changes and reload data
watch(() => skillId.value, () => {
    loadSkillData();
});

</script>

<style scoped>
.form-label {
    color: #7b809a;
}

.d-flex {
    display: flex;
}

.align-items-center {
    align-items: center;
}

.gap-2 {
    gap: 0.5rem;
}
</style>
